import React, { useEffect, useState } from "react";
import Button from "../components/Buttons/Button";
import GalCard from "../components/Gallery-cards/GalCard";
import { Plus, ArrowLeft, ArrowRight } from "lucide-react";
import AddMealModal from "../components/models/AddMealModal";
import EditMealModal from "../components/models/EditMealModal";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchCategory,
  fetchMealDetail,
  fetchCountry,
  deleteMealdetail,
} from "../redux/slices/gallerySlice";
import Loader from "../components/Loader";
import MealShimmerCard from "../components/Shimmers/MealShimmerCard";
const Mealgallery = () => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage] = useState(1);

  const {
    categoryList,
    countryList,
    mealList,
    loading,
    currentPage,
    totalPages,
  } = useSelector((state) => state.gallery);

  const isMealGalleryLoading = loading && !initialLoad;

  // -------------------- INITIAL FETCH --------------------
  useEffect(() => {
    const loadInitial = async () => {
      await dispatch(fetchCategory());
      await dispatch(fetchCountry());
      await dispatch(
        fetchMealDetail({ page: 1, country_id: "", category_id: "" })
      );
      setInitialLoad(false);
    };
    loadInitial();
  }, [dispatch]);

  // -------------------- FETCH ON FILTER/PAGE CHANGE --------------------
  useEffect(() => {
    if (!initialLoad) {
      dispatch(
        fetchMealDetail({
          page,
          country_id: selectedCountry ? Number(selectedCountry) : "",
          category_id: selectedCategory ? Number(selectedCategory) : "",
        })
      );
    }
  }, [page, selectedCountry, selectedCategory, dispatch, initialLoad]);

  // -------------------- DELETE MEAL --------------------
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the image permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteMealdetail(id)).then(() => {
          Swal.fire("Deleted!", "Image has been deleted.", "success");
          if (mealList.length === 1 && page > 1) {
            setPage((prev) => prev - 1);
          } else {
            dispatch(
              fetchMealDetail({
                page,
                country_id: selectedCountry ? Number(selectedCountry) : "",
                category_id: selectedCategory ? Number(selectedCategory) : "",
              })
            );
          }
        });
      }
    });
  };

  // -------------------- MODAL HANDLERS --------------------
  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };
  const handleEdit = (meal) => {
    setEditData(meal);
    setEditModalOpen(true);
  };

  // -------------------- PAGINATION --------------------
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const handlePrevious = () => {
      if (!isMealGalleryLoading && page > 1) setPage((prev) => prev - 1);
    };
    const handleNext = () => {
      if (!isMealGalleryLoading && page < totalPages)
        setPage((prev) => prev + 1);
    };
    const handlePageClick = (p) => {
      if (!isMealGalleryLoading) setPage(p);
    };

    return (
      <div className="flex justify-center py-6">
        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            disabled={page === 1 || isMealGalleryLoading}
            className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageClick(i + 1)}
                className={`px-3 py-1 rounded text-sm ${
                  page === i + 1
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={page === totalPages || isMealGalleryLoading}
            className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // -------------------- RETURN --------------------
  if (initialLoad) return <Loader />; // full-page loader on first load

  return (
    <div className="relative">
      {/* PAGE TITLE & ADD BUTTON */}
      <div className="flex flex-col md:flex-row justify-between border-b border-gray-200 py-4 gap-4">
        <h1 className="text-2xl text-gray-800 font-bold">
          Meal Gallery Management
        </h1>
        <Button
          classes="w-full sm:w-[200px] hover:text-white"
          text="Add New Image"
          icon={<Plus size={20} />}
          iconPosition="left"
          widthSize={200}
          onClick={handleAdd}
        />
      </div>

      {/* FILTERS */}
      <div className="py-4 flex flex-col md:flex-row gap-4">
        {/* Country */}
        <div className="flex items-center gap-2 bg-white shadow-md rounded-lg px-4 py-2 hover:shadow-lg transition-shadow duration-300">
          <label
            htmlFor="selectCountry"
            className="text-gray-700 font-medium w-24"
          >
            Country:
          </label>
          <select
            id="selectCountry"
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setPage(1);
            }}
            className="flex-1 p-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FF6943]/40 cursor-pointer"
          >
            <option value="">All</option>
            {countryList?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.country_name}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="flex items-center gap-2 bg-white shadow-md rounded-lg px-4 py-2 hover:shadow-lg transition-shadow duration-300">
          <label
            htmlFor="selectCategory"
            className="text-gray-700 font-medium w-24"
          >
            Category:
          </label>
          <select
            id="selectCategory"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
            className="flex-1 p-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FF6943]/40 cursor-pointer"
          >
            <option value="">All</option>
            {categoryList?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* MEAL CARDS */}
      <div className="py-4 relative">
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 py-4">
          {isMealGalleryLoading ? (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <MealShimmerCard key={i} />
              ))}
            </>
          ) : mealList?.length > 0 ? (
            mealList.map((meal) => (
              <GalCard
                key={meal.id}
                mealId={meal.id}
                cardCategory={meal.category_master}
                countryName={meal.country}
                cardImage={meal.image}
                cardTitle={meal.name}
                showMainToggle={false}
                status={meal.status}
                showButtons
                showSmallToggle
                onDelete={() => handleDelete(meal.id)}
                onEdit={() => handleEdit(meal)}
              />
            ))
          ) : (
            <p>No meals found</p>
          )}
        </div>
      </div>

      {/* PAGINATION */}
      {renderPagination()}

      {/* MODALS */}
      <AddMealModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <EditMealModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        editData={editData}
        mealId={editData?.id}
        currentPage={page}
        selectedCountry={selectedCountry}
        selectedCategory={selectedCategory}
      />
    </div>
  );
};

export default Mealgallery;
